import { BOOKING_SPACE_STATUS } from '../enum/space-status.enum';

export class BookingSpace {
  readonly id: string;
  readonly organizationId: string;
  readonly zoneId?: string;
  readonly number: number;
  readonly name: string;
  readonly description?: string;
  readonly capacity: number;
  readonly rateIds: string[];

  private _status: BOOKING_SPACE_STATUS;

  constructor(
    id: string,
    organizationId: string,
    number: number,
    name: string,
    capacity: number,
    status: BOOKING_SPACE_STATUS,
    rateIds: string[],
    zoneId?: string,
    description?: string
  ) {
    this.id = id;
    this.organizationId = organizationId;
    this.zoneId = zoneId;
    this.number = number;
    this.name = name;
    this.description = description;
    this.capacity = capacity;
    this._status = status;
    this.rateIds = rateIds;
  }

  get status(): BOOKING_SPACE_STATUS {
    return this._status;
  }

  get displayName(): string {
    return `${this.name} ${this.number}`;
  }

  get displayNumber(): string {
    return this.number.toString();
  }

  get isAvailable(): boolean {
    return this._status === BOOKING_SPACE_STATUS.AVAILABLE;
  }

  get isOccupied(): boolean {
    return this._status === BOOKING_SPACE_STATUS.OCCUPIED;
  }

  get hasZone(): boolean {
    return !!this.zoneId;
  }

  get hasRates(): boolean {
    return this.rateIds.length > 0;
  }

  canAccommodate(bookingSize: number): boolean {
    return this.capacity >= bookingSize && this.isAvailable;
  }

  updateStatus(newStatus: BOOKING_SPACE_STATUS): void {
    this._status = newStatus;
  }

  occupy(): void {
    if (this.isAvailable) {
      this.updateStatus(BOOKING_SPACE_STATUS.OCCUPIED);
    }
  }

  release(): void {
    if (this.isOccupied) {
      this.updateStatus(BOOKING_SPACE_STATUS.AVAILABLE);
    }
  }

  setOutOfService(): void {
    this.updateStatus(BOOKING_SPACE_STATUS.OUT_OF_SERVICE);
  }

  setMaintenance(): void {
    this.updateStatus(BOOKING_SPACE_STATUS.MAINTENANCE);
  }

  hasRate(rateId: string): boolean {
    return this.rateIds.includes(rateId);
  }

  addRate(rateId: string): void {
    if (!this.hasRate(rateId)) {
      (this.rateIds as string[]).push(rateId);
    }
  }

  removeRate(rateId: string): void {
    const index = this.rateIds.indexOf(rateId);

    if (index > -1) {
      (this.rateIds as string[]).splice(index, 1);
    }
  }
}
